import React, { useEffect, useState } from 'react';
import Box from '../Box/Box';
import name from './name';
import InputError from '../InputError';
import TextFieldCharacterCountLabel from './TextFieldCharacterCountLabel';
import InputHelper from '../InputHelper';

type TTextFieldFooter = {
  helperText?: string;
  errorText?: string;
  charsRemainingText?: string;
  charsExceededText?: string;
  maxLength?: number;
  characterCount?: number;
  isLive?: boolean;
  id?: string;
  errorTextId?: string;
  charsExceededId?: string;
  charsExceeded?: boolean;
};

const getLiveAnnouncement = (
  charsExceeded: boolean,
  exceededAmount: number,
  charsExceededText: string,
  errorText?: string,
  shouldShowCharacterCount?: boolean,
  characterCount?: number,
  maxLength?: number,
  // eslint-disable-next-line max-params
) => {
  const announcements: string[] = [];

  // Character limit errors have priority (announced first)
  // Only announce on focus if there's an error
  if (charsExceeded) {
    announcements.push(`Error: ${charsExceededText} by ${exceededAmount} characters`);
  }

  // Then other validation errors on focus
  if (errorText) {
    announcements.push(`Error: ${errorText}`);
  }

  // Character count feedback (non-error state)
  if (!charsExceeded && !errorText && shouldShowCharacterCount && maxLength) {
    announcements.push(`${characterCount} of ${maxLength} characters used`);
  }

  return announcements.join('. ');
};

const hasMaxLength = (maxLength: number) => maxLength > 0;
const getExceededAmount = (characterCount: number, maxLength: number) => characterCount - maxLength;
const shouldShowCharacterCount = (maxLengthExists: boolean, charsExceeded: boolean, errorText?: string) =>
  maxLengthExists && (charsExceeded || !errorText);

const shouldDisplayHelperText = (helperText?: string, errorText?: string, charsExceeded?: boolean) =>
  helperText && !errorText && !charsExceeded;

const shouldDisplayCharsExceeded = (charsExceeded: boolean) => charsExceeded;
const shouldDisplayErrorText = (errorText?: string) => Boolean(errorText);

const TextFieldFooter = ({
  errorText,
  helperText,
  characterCount = 0,
  charsRemainingText,
  charsExceededText = 'Character limit exceeded',
  maxLength = 0,
  isLive = true,
  id,
  errorTextId,
  charsExceededId,
  charsExceeded = false,
}: TTextFieldFooter) => {
  const maxLengthExists = hasMaxLength(maxLength);
  const exceededAmount = getExceededAmount(characterCount, maxLength);
  const shouldDisplayCharacterCount = shouldShowCharacterCount(maxLengthExists, charsExceeded, errorText);

  const [liveAnnouncement, setLiveAnnouncement] = useState('');

  useEffect(() => {
    setLiveAnnouncement(
      getLiveAnnouncement(
        charsExceeded,
        exceededAmount,
        charsExceededText,
        errorText,
        shouldDisplayCharacterCount,
        characterCount,
        maxLength,
      ),
    );
  }, [
    charsExceeded,
    exceededAmount,
    errorText,
    characterCount,
    maxLength,
    shouldDisplayCharacterCount,
    charsExceededText,
  ]);

  return (
    <Box parent={name} name="footer" as="div">
      <div>
        {shouldDisplayHelperText(helperText, errorText, charsExceeded) && (
          <InputHelper id={id}>{helperText || ''}</InputHelper>
        )}
        {/* Character limit errors displayed first with separate ID */}
        {shouldDisplayCharsExceeded(charsExceeded) && <InputError errorText={charsExceededText} id={charsExceededId} />}
        {/* Validation errors displayed second */}
        {shouldDisplayErrorText(errorText) && <InputError errorText={errorText || ''} id={errorTextId} />}
      </div>

      {shouldDisplayCharacterCount && (
        <TextFieldCharacterCountLabel
          characterCount={characterCount}
          charsRemainingText={charsRemainingText}
          maxLength={maxLength}
          aria-live="polite"
          aria-describedby="live-announcement"
        />
      )}

      <div
        id="live-announcement"
        role="alert"
        aria-live={isLive || errorText || charsExceeded ? 'polite' : 'off'}
        aria-atomic="true"
        style={{
          position: 'absolute',
          height: 0,
          width: 0,
          overflow: 'hidden',
          display: liveAnnouncement ? 'block' : 'none',
        }}
      >
        {liveAnnouncement}
      </div>
    </Box>
  );
};

export default TextFieldFooter;
